"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import {
  getGeneratedDesignSpec,
  getGeneratedFrontDesignSpec,
} from "@/lib/designer/savedDesign";
import { projectMobileAttentionContent } from "@/lib/designer/mobileAttentionContent";
import { projectSavedBackStaleness } from "@/lib/designer/previewFreshness";
import {
  hasRenderableChatTranscript,
  isPresentableChatWidget,
  isRenderableChatMessage,
} from "@/lib/agent/renderableChatMessage";
import { resolveDesignerChoice } from "@/lib/designer/actionProjection";
import type { AgentActionPayload, PresetOption } from "@/types/createAgent";
import { restoreDesignDraft } from "@/lib/designer/restoreDesignDraft";
import {
  isDesignWorkspaceBusy,
  planDesignChanges,
} from "@/lib/designer/applyDesignChanges";
import type { DesignSpec } from "@/types/designSpec";
import { projectDesignerWorkspaceActions } from "@/lib/designer/workspaceActions";
import {
  designerRenderFooterLabel,
  projectDesignerRenderControl,
} from "@/lib/designer/renderControl";
import { cn } from "@/lib/utils";
import { useCreatePageStore } from "@/stores/createPageStore";
import PendingDesignActionDialog, {
  type PendingDesignAction,
} from "./PendingDesignActionDialog";
import { useChatActions } from "./ChatActionsProvider";
import type { DesktopRenderActionState } from "./desktopRenderAction";
import { useApplyDesignChanges } from "./useApplyDesignChanges";
import MobileAttentionPanel, {
  type MobileAttentionAction,
  type MobileAttentionViewModel,
} from "./MobileAttentionPanel";
import { useDesignerWorkspace } from "./DesignerWorkspaceProvider";
import { useDesignPreviewSync } from "./useDesignPreviewSync";
import DesignDetailsInspector, {
  pendingDesignSummary,
} from "./DesignDetailsInspector";
import type { TryOnWorkspaceStatus } from "./VirtualTryOnDialog";
import FindSimilarDresses from "./FindSimilarDresses";
import ChatGenerationProgress from "./chat/ChatGenerationProgress";
import AgentPresets from "./presets/AgentPresets";
import DesignPreviewPortrait, {
  type DressDesignView,
} from "./DesignPreviewPortrait";
import {
  DesignPreviewHeader,
  DesignSecondaryActions,
  DesktopDesignActions,
} from "./DesignPreviewControls";

export type { DressDesignView } from "./DesignPreviewPortrait";

export type MobileFrontReviewIntent = "modify_front" | "design_back";

interface DesktopDressDesignCanvasProps {
  className?: string;
  mobileVisible?: boolean;
  mobileAttentionHost?: HTMLDivElement | null;
  mobileComposerBusy?: boolean;
  onShowMobileView?: (view: "chat" | "front" | "back" | "details") => void;
  showInlineDetails?: boolean;
  tryOnStatus?: TryOnWorkspaceStatus;
  onViewTryOn?: () => void;
  onEditInChat?: (intent?: MobileFrontReviewIntent) => void;
  detailsOpen: boolean;
  detailsPanelId: string;
  detailsTriggerRef: React.RefObject<HTMLButtonElement | null>;
  onDetailsOpenChange: (open: boolean) => void;
  showStartOver?: boolean;
  startOverDisabled?: boolean;
  onStartOver?: () => void;
  tryOnDisabled?: boolean;
  tryOnDisabledReason?: string;
  onTryOn?: () => void;
  onOpenFullscreen?: (view: DressDesignView) => void;
  desktopRenderActionState?: DesktopRenderActionState;
}

/** One responsive surface owns images, details and actions independently of chat. */
export default function DesktopDressDesignCanvas({
  className,
  mobileVisible = false,
  mobileAttentionHost,
  mobileComposerBusy = false,
  onShowMobileView,
  showInlineDetails = true,
  tryOnStatus,
  onViewTryOn,
  onEditInChat,
  detailsOpen,
  detailsPanelId,
  detailsTriggerRef,
  onDetailsOpenChange,
  showStartOver = false,
  startOverDisabled = false,
  onStartOver,
  tryOnDisabled = false,
  tryOnDisabledReason,
  onTryOn,
  onOpenFullscreen,
  desktopRenderActionState,
}: DesktopDressDesignCanvasProps) {
  const titleId = useId();
  const helperId = useId();
  const [detailsEditing, setDetailsEditing] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    action: PendingDesignAction;
    scope: string;
    draftKey: string;
    planKey: string;
    capabilityKey?: string;
  } | null>(null);
  const [continuing, setContinuing] = useState(false);
  const secondaryInFlight = useRef(false);
  const mounted = useRef(true);
  const secondaryTrigger = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const projectEpoch = useCreatePageStore(state => state.projectActionEpoch);
  const agentSessionId = useCreatePageStore(state => state.agent.sessionId);
  const { generateImages, handleCreateTicket } = useChatActions();
  const workspace = useDesignerWorkspace();
  const { isLoading: creditsLoading, isFetching: creditsFetching } =
    useCreditsContext();
  const images = useCreatePageStore(state => state.generation.images);
  const frontImageUrl = images.front.url?.trim() || null;
  const backImageUrl = images.back.url?.trim() || null;
  const activeView = useCreatePageStore(state => state.ui.view);
  const setCurrentView = useCreatePageStore(state => state.setCurrentView);
  const phase = useCreatePageStore(state => state.agent.phase);
  const conversation = useCreatePageStore(state => state.agent.conversation);
  const agentRequestInFlight = useCreatePageStore(state =>
    Boolean(state.agent.activeRequestController)
  );
  const formData = useCreatePageStore(state => state.form.data);
  const loading = useCreatePageStore(
    state =>
      state.generation.progress.loading || state.ui.modification.isModifying
  );
  const generationPhase = useCreatePageStore(
    state => state.generation.progress.phase
  );
  const quoteSubmitting = useCreatePageStore(
    state => state.ticket.designSubmitted
  );
  const generationError = useCreatePageStore(
    state => state.generation.errors?.message
  );
  const quoteError = useCreatePageStore(state => state.ticket.submissionError);
  const operationError = generationError || quoteError;
  const apply = useApplyDesignChanges();
  // Share the committed draft used by Details and preview requests; flat form
  // fields can lag behind a conversational correction in the canonical brief.
  const currentDesign = apply.draft?.visual;
  const frontSync = useDesignPreviewSync("front");
  const backSync = useDesignPreviewSync("back");
  const unknownPreview =
    frontSync.kind === "unknown" || backSync.kind === "unknown";
  const renderControl = projectDesignerRenderControl({
    phase,
    activeView,
    frontImageUrl,
    frontImagePath: images.front.path?.trim() || null,
    backImageUrl,
    formState: formData,
    renderActionState: desktopRenderActionState,
    apply,
    detailsEditing,
    creditsLoading: creditsLoading || creditsFetching,
  });
  const capability = renderControl.usesCapability
    ? desktopRenderActionState?.capability
    : null;
  const plannedSides =
    capability?.applyPlan?.pendingSides ?? apply.pendingSides;
  const {
    hasDesign,
    hasPendingChanges,
    renderSide,
    needsCommittedApply,
    actionReason,
    canRender,
    creditCost,
  } = renderControl;
  const mobileRenderActionLabel = `${
    renderControl.renderId === "generate_dress_back" && !backImageUrl
      ? "Generate"
      : hasPendingChanges
        ? "Apply"
        : renderControl.renderId === "generate_dress_front"
          ? "Generate"
          : "Apply"
  } · ${renderControl.creditCost} ${renderControl.creditCost === 1 ? "credit" : "credits"}`;
  const imageActionPending = loading || Boolean(apply.busyStep);
  const generatedSpec = getGeneratedDesignSpec(images);
  const searchPreviewSpec = getGeneratedFrontDesignSpec(images.front);
  const workspaceActions = projectDesignerWorkspaceActions({
    phase:
      hasPendingChanges && generatedSpec
        ? backImageUrl
          ? "back_review"
          : "front_review"
        : phase,
    formState:
      hasPendingChanges && generatedSpec
        ? { ...formData, ...restoreDesignDraft(generatedSpec).formData }
        : formData,
    designSpec:
      hasPendingChanges && generatedSpec ? generatedSpec : apply.draft,
    hasFrontImage: Boolean(frontImageUrl),
    hasBackImage: Boolean(backImageUrl),
    storedFrontImagePath: images.front.path,
    workspaceBusy: apply.busy || detailsEditing || continuing,
    detailsEditing,
    quoteSubmitting,
    hasPendingRenderedChanges: hasPendingChanges,
    tryOnAvailable: Boolean(onTryOn),
    tryOnDisabled,
    tryOnDisabledReason,
  });
  const showMobileDetails = detailsOpen && !showInlineDetails;
  const changeSummary = pendingDesignSummary(apply.changes);
  const pendingSides = plannedSides.join(" and ");
  const step = imageActionPending
    ? (apply.busyStep ??
      (generationPhase === "front" || generationPhase === "back"
        ? generationPhase
        : renderSide))
    : null;
  const summaryLabel = step
    ? `Applying ${step}${step === "back" && apply.pendingSides.length > 1 ? " · 2 of 2" : ""}`
    : apply.failedSide
      ? `${apply.failedSide === "back" ? "Back" : "Front"} changes need another try`
      : hasPendingChanges
        ? `Changes to ${pendingSides}`
        : renderControl.renderId === "generate_dress_back" &&
            !backImageUrl &&
            renderControl.hasAction
          ? "Back ready to preview"
          : hasDesign
            ? unknownPreview
              ? "Saved preview"
              : "All changes applied"
            : "Your next preview";
  const summaryValue =
    step === "back"
      ? "Front saved · Matching the back"
      : step
        ? "Your saved previews stay visible"
        : apply.failedSide === "back"
          ? "Front saved · Back still pending"
          : changeSummary ||
            (hasDesign
              ? backImageUrl
                ? "Front and back saved"
                : renderControl.renderId === "generate_dress_back" &&
                    renderControl.hasAction
                  ? "Front saved · Back design ready"
                  : "Front saved"
              : "Bring your dress to life");
  const handleRender = () => {
    if (!canRender) return;
    if (needsCommittedApply) void apply.apply();
    else if (capability) void capability.execute();
    else void generateImages(renderSide === "front" ? 2 : 4, true);
  };
  const scope = JSON.stringify([
    projectEpoch,
    agentSessionId,
    images.front.url,
    images.front.path,
    images.back.url,
    images.back.path,
  ]);
  const draftKey = JSON.stringify(apply.draft);
  const planKey = JSON.stringify([
    apply.steps,
    apply.deferredSides,
    apply.creditCost,
    capability?.applyPlan,
  ]);
  const confirmationCurrent = Boolean(
    confirmation &&
    confirmation.scope === scope &&
    confirmation.draftKey === draftKey &&
    confirmation.planKey === planKey &&
    confirmation.capabilityKey === capability?.key
  );
  useEffect(() => {
    if (confirmation && !confirmationCurrent) setConfirmation(null);
  }, [confirmation, confirmationCurrent]);
  const confirmationIsCurrentNow = () => {
    if (!confirmation) return false;
    const current = useCreatePageStore.getState();
    const currentImages = current.generation.images;
    const currentScope = JSON.stringify([
      current.projectActionEpoch,
      current.agent.sessionId,
      currentImages.front.url,
      currentImages.front.path,
      currentImages.back.url,
      currentImages.back.path,
    ]);
    const currentPlan = planDesignChanges(current);
    return (
      confirmation.scope === currentScope &&
      confirmation.draftKey === JSON.stringify(currentPlan.draft) &&
      confirmation.planKey ===
        JSON.stringify([
          currentPlan.steps,
          currentPlan.deferredSides,
          currentPlan.creditCost,
          capability?.applyPlan,
        ]) &&
      confirmation.capabilityKey === capability?.key
    );
  };
  const dismissConfirmation = () => setConfirmation(null);
  const reportUnverifiableQuote = () =>
    useCreatePageStore
      .getState()
      .submitError(
        "This saved preview could not be verified for a quote. Generate a new preview, then try again."
      );
  const executeSecondary = async (
    action: PendingDesignAction,
    spec?: DesignSpec
  ) => {
    if (action === "tryOn") {
      onTryOn?.();
      return;
    }
    const currentImages = useCreatePageStore.getState().generation.images;
    // A retained Back preview can describe an older Front. Until it is
    // explicitly updated, quote the current Front rather than presenting the
    // mismatched pair as one finished dress.
    const frontOnly =
      !currentImages.back.url?.trim() ||
      projectSavedBackStaleness(currentImages).stale;
    const savedSpec =
      !currentImages.front.path?.trim() ||
      (!frontOnly && !currentImages.back.path?.trim())
        ? null
        : frontOnly
          ? getGeneratedFrontDesignSpec(currentImages.front)
          : (spec ?? getGeneratedDesignSpec(currentImages));
    if (!savedSpec) {
      reportUnverifiableQuote();
      return;
    }
    await handleCreateTicket(
      savedSpec,
      frontOnly ? { quoteScope: "front_only" } : undefined
    );
  };
  const requestSecondary = (
    action: PendingDesignAction,
    trigger: HTMLButtonElement
  ) => {
    if (!workspaceActions[action === "tryOn" ? "tryOn" : "quote"].enabled)
      return;
    const current = useCreatePageStore.getState();
    const currentPlan = planDesignChanges(current);
    // A click may arrive before React has painted a newer store revision.
    if (
      secondaryInFlight.current ||
      isDesignWorkspaceBusy(current) ||
      current.projectActionEpoch !== projectEpoch ||
      current.agent.sessionId !== agentSessionId ||
      current.generation.images !== images ||
      JSON.stringify(currentPlan.draft) !== draftKey ||
      JSON.stringify([
        currentPlan.steps,
        currentPlan.deferredSides,
        currentPlan.creditCost,
        capability?.applyPlan,
      ]) !== planKey
    )
      return;
    if (hasPendingChanges) {
      secondaryTrigger.current = trigger;
      setConfirmation({
        action,
        scope,
        draftKey,
        planKey,
        capabilityKey: capability?.key,
      });
    } else {
      void executeSecondary(action);
    }
  };
  const continueWithGenerated = async () => {
    if (
      !confirmation ||
      !confirmationIsCurrentNow() ||
      secondaryInFlight.current ||
      isDesignWorkspaceBusy(useCreatePageStore.getState())
    )
      return;
    const currentGeneratedSpec = getGeneratedDesignSpec(
      useCreatePageStore.getState().generation.images
    );
    if (confirmation.action === "quote" && !currentGeneratedSpec) {
      dismissConfirmation();
      reportUnverifiableQuote();
      return;
    }
    const action = confirmation.action;
    secondaryInFlight.current = true;
    setContinuing(true);
    dismissConfirmation();
    try {
      await executeSecondary(action, currentGeneratedSpec ?? undefined);
    } finally {
      secondaryInFlight.current = false;
      if (mounted.current) setContinuing(false);
    }
  };
  const applyFirst = async () => {
    if (
      !confirmation ||
      !confirmationIsCurrentNow() ||
      !canRender ||
      secondaryInFlight.current
    )
      return;
    const action = confirmation.action;
    const epoch = projectEpoch;
    const sessionId = agentSessionId;
    secondaryInFlight.current = true;
    setContinuing(true);
    dismissConfirmation();
    try {
      const succeeded = capability?.applyPlan
        ? await capability.execute({ suppressAgentReview: true })
        : await apply.apply({ suppressAgentReview: true });
      const current = useCreatePageStore.getState();
      if (
        succeeded !== true ||
        !mounted.current ||
        current.projectActionEpoch !== epoch ||
        current.agent.sessionId !== sessionId ||
        isDesignWorkspaceBusy(current) ||
        planDesignChanges(current).hasPendingChanges
      )
        return;
      const saved = getGeneratedDesignSpec(current.generation.images);
      if (action === "quote" && !saved) {
        reportUnverifiableQuote();
        return;
      }
      await executeSecondary(action, saved ?? undefined);
    } finally {
      secondaryInFlight.current = false;
      if (mounted.current) setContinuing(false);
    }
  };
  const selectTab = (tab: string) => {
    if (tab === "details") onDetailsOpenChange(true);
    else {
      setCurrentView(tab as DressDesignView);
      if (!showInlineDetails) onDetailsOpenChange(false);
    }
  };
  const secondaryActions = (
    <DesignSecondaryActions
      actions={workspaceActions}
      hasPendingChanges={hasPendingChanges}
      quoteSubmitting={quoteSubmitting}
      onRequest={requestSecondary}
    />
  );
  const inspector = (
    <DesignDetailsInspector
      key={`${projectEpoch}:${agentSessionId}:${images.front.path}:${images.back.path}:${showInlineDetails ? activeView : "mobile"}`}
      side={activeView}
      changes={apply.changes}
      compact={!showInlineDetails}
      onEditingChange={setDetailsEditing}
      onEditInChat={onEditInChat}
      onRevertChanges={
        !showInlineDetails &&
        apply.canUndo &&
        !apply.busy &&
        !detailsEditing &&
        !continuing
          ? apply.undo
          : undefined
      }
      secondaryActions={!showInlineDetails ? workspaceActions : undefined}
      onSecondaryAction={!showInlineDetails ? requestSecondary : undefined}
      onStartOver={showStartOver ? onStartOver : undefined}
      startOverDisabled={startOverDisabled || apply.busy || continuing}
      onClose={() => {
        onDetailsOpenChange(false);
        detailsTriggerRef.current?.focus();
      }}
    />
  );
  const agentBusy = Boolean(
    conversation.isTyping ||
    conversation.presetsLoading ||
    agentRequestInFlight ||
    workspace?.interaction.processingPresetId ||
    mobileComposerBusy
  );
  const attentionContent = projectMobileAttentionContent({
    messages: conversation.messages,
    busy: agentBusy || imageActionPending,
  });
  const latestRenderableAssistant = conversation.messages.findLastIndex(
    message => message.role === "assistant" && isRenderableChatMessage(message)
  );
  const latestSubmittedUser = conversation.messages.findLastIndex(
    message => message.role === "user"
  );
  const awaitingNewReply = latestSubmittedUser > latestRenderableAssistant;
  const activeWidgetMessage = attentionContent
    ? conversation.messages.find(
        message => message.id === attentionContent.messageId
      )
    : undefined;
  const activeWidgets = (activeWidgetMessage?.widgets ?? []).filter(
    isPresentableChatWidget
  );
  const activeMessagePresets =
    activeWidgetMessage?.presets !== undefined ||
    activeWidgetMessage?.widgets !== undefined
      ? (activeWidgetMessage?.presets ?? [])
      : (workspace?.visiblePresets ?? []);
  const selectedWidgetLabel = activeWidgets
    .map(widget => widget.selection?.data?.selectedLabel)
    .find((label): label is string => typeof label === "string");
  const widgetMessageId = activeWidgetMessage?.id ?? "";
  const resolveWidgetSource = (
    preset: PresetOption | null,
    source?: AgentActionPayload["source"]
  ) =>
    preset
      ? resolveDesignerChoice(widgetMessageId, preset, activeWidgets, source)
          ?.source
      : { messageId: widgetMessageId };
  // Render the same choice component and dispatch paths as the transcript.
  // Its widget coordinates keep selection, uploads and paid actions tied to
  // the exact current assistant turn, rather than to a copied label.
  const activeChatWidget =
    mobileVisible &&
    attentionContent?.hasActions &&
    activeWidgetMessage &&
    workspace ? (
      <AgentPresets
        key={activeWidgetMessage.id}
        messageId={activeWidgetMessage.id}
        presets={activeMessagePresets}
        widgets={activeWidgets}
        selectedPresetLabel={
          workspace.interaction.selectedPresetLabelsByMessageId[
            activeWidgetMessage.id
          ] ??
          selectedWidgetLabel ??
          null
        }
        processingPresetId={workspace.interaction.processingPresetId}
        disabled={
          agentBusy ||
          imageActionPending ||
          detailsEditing ||
          Boolean(quoteSubmitting) ||
          workspace.interaction.authLoading
        }
        onPresetClick={(preset, origin) => {
          const source = resolveWidgetSource(preset, origin);
          if (source)
            void workspace.interaction.handlePresetClick(preset, source);
        }}
        onPresetSelect={(preset, origin) => {
          const source = resolveWidgetSource(preset, origin);
          if (source)
            workspace.interaction.handlePresetSelect(
              activeWidgetMessage.id,
              preset,
              source
            );
        }}
        onPresetGenerate={(preset, origin) => {
          const source = resolveWidgetSource(preset, origin);
          if (source)
            void workspace.interaction.handleSelectedPresetGenerate(
              activeWidgetMessage.id,
              preset,
              source
            );
        }}
        onPresetChatMore={(preset, origin) => {
          const source = resolveWidgetSource(preset, origin);
          if (source)
            void workspace.interaction.handleSelectedPresetChatMore(
              activeWidgetMessage.id,
              preset,
              source
            );
        }}
        onComposerModifyRequest={() => onEditInChat?.("modify_front")}
        compactDock
      />
    ) : null;
  const renderAction: MobileAttentionAction | undefined =
    renderControl.hasAction && canRender
      ? {
          id: "render-preview",
          label: mobileRenderActionLabel,
          onClick: handleRender,
        }
      : undefined;
  const openChatFromStatus =
    mobileVisible && onShowMobileView
      ? () => onShowMobileView("chat")
      : undefined;
  let mobileAttentionModel: MobileAttentionViewModel;
  // Active work takes the panel back from a failure left by an earlier action.
  if (
    imageActionPending ||
    quoteSubmitting ||
    tryOnStatus?.status === "generating"
  ) {
    mobileAttentionModel = {
      kind: "generating",
      label: imageActionPending
        ? `Creating ${step === "back" ? "back" : "front"} preview…`
        : quoteSubmitting
          ? "Preparing your quote…"
          : "Creating your try-on…",
      onOpenChat: openChatFromStatus,
    };
  } else if (agentBusy || awaitingNewReply) {
    mobileAttentionModel = {
      kind: "thinking",
      label: "Your designer is thinking…",
      onOpenChat: openChatFromStatus,
    };
  } else if (detailsEditing) {
    mobileAttentionModel = {
      kind: "editing",
      label: "Editing design details",
    };
  } else if (generationError || apply.failedSide) {
    mobileAttentionModel = {
      kind: "error",
      label: "Preview needs another try",
      detail:
        generationError ||
        "Your changes are saved. Try the preview again when you're ready.",
      primaryAction: canRender
        ? { id: "retry-preview", label: "Retry", onClick: handleRender }
        : undefined,
    };
  } else if (quoteError) {
    mobileAttentionModel = {
      kind: "error",
      label: "Quote needs another try",
      detail: quoteError,
      primaryAction: workspaceActions.quote.enabled
        ? {
            id: "retry-quote",
            label: "Retry quote",
            onClick: event => requestSecondary("quote", event.currentTarget),
          }
        : undefined,
    };
  } else if (tryOnStatus?.status === "error") {
    mobileAttentionModel = {
      kind: "error",
      label: "Try-on needs another try",
      detail: tryOnStatus.error ?? "Open try-on to try again.",
      primaryAction: workspaceActions.tryOn.enabled
        ? {
            id: "retry-try-on",
            label: "Retry try-on",
            onClick: event => requestSecondary("tryOn", event.currentTarget),
          }
        : undefined,
    };
  } else if (conversation.error) {
    mobileAttentionModel = {
      kind: "error",
      label: "Your message needs attention",
      detail: conversation.error,
    };
  } else if (
    activeView === "back" &&
    backImageUrl &&
    backSync.savedBackStale &&
    !hasPendingChanges
  ) {
    mobileAttentionModel = {
      kind: "next-step",
      label: "Back is out of date",
      detail:
        "Your front changed after this back preview was made. Update it when you're ready; until then, a quote uses the current front only.",
      primaryAction: {
        id: "update-saved-back",
        label: "Update · 1 credit",
        onClick: backSync.updateBackFromSavedFront,
        disabled: backSync.updateDisabled || creditsLoading || creditsFetching,
      },
    };
  } else if (renderAction) {
    mobileAttentionModel = {
      kind: "ready",
      identity: attentionContent?.messageId,
      label:
        renderControl.renderId === "generate_dress_back" && !backImageUrl
          ? "Back ready"
          : hasPendingChanges
            ? "Changes ready"
            : renderSide === "back"
              ? "Back ready"
              : "Front ready",
      primaryAction: renderAction,
      content: activeChatWidget,
    };
  } else if (tryOnStatus?.status === "complete" && onViewTryOn) {
    mobileAttentionModel = {
      kind: "next-step",
      label: "Your try-on is ready",
      primaryAction: {
        id: "view-try-on",
        label: "View try-on",
        onClick: onViewTryOn,
      },
    };
  } else if (attentionContent?.hasActions) {
    mobileAttentionModel = {
      kind: "next-step",
      identity: attentionContent.messageId,
      label: attentionContent.headline,
      content: activeChatWidget,
    };
  } else if (frontImageUrl) {
    const restoredDecision = !hasRenderableChatTranscript(
      conversation.messages
    );
    mobileAttentionModel = {
      kind: "next-step",
      label: backImageUrl
        ? "Your previews are ready"
        : "Front preview is ready",
      primaryAction:
        restoredDecision && mobileVisible && onShowMobileView
          ? {
              id: "restored-next-steps",
              label: "See next steps",
              onClick: () => onShowMobileView("chat"),
            }
          : undefined,
    };
  } else {
    mobileAttentionModel = {
      kind: "idle",
      label: "Keep designing in chat",
    };
  }
  const chatHasActionablePanel = Boolean(
    mobileAttentionModel.primaryAction &&
    !mobileAttentionModel.primaryAction.disabled
  );
  const renderPortrait = (side: DressDesignView, showCaption = false) => {
    const staleAction =
      side === "back" && backSync.savedBackStale && !hasPendingChanges
        ? {
            label: "Update back · 1 credit",
            onClick: backSync.updateBackFromSavedFront,
            disabled:
              backSync.updateDisabled || creditsLoading || creditsFetching,
          }
        : undefined;
    const sideHasRenderPlan =
      !showInlineDetails &&
      renderControl.hasAction &&
      (canRender || renderControl.readiness.allowed) &&
      (renderSide === side ||
        (hasPendingChanges && plannedSides.includes(side)));
    const panelOffersRender =
      mobileAttentionModel.kind === "ready" &&
      mobileAttentionModel.primaryAction?.id === "render-preview";
    return (
      <DesignPreviewPortrait
        side={side}
        imageUrl={side === "front" ? frontImageUrl : backImageUrl}
        syncKind={side === "front" ? frontSync.kind : backSync.kind}
        showCaption={showCaption}
        staleAction={staleAction}
        showInlineStaleAction={showInlineDetails && !showCaption}
        showEmptyStand={showInlineDetails && !hasDesign}
        generating={imageActionPending && step === side}
        readyActionLabel={
          sideHasRenderPlan ? mobileRenderActionLabel : undefined
        }
        followPanel={
          (sideHasRenderPlan && !panelOffersRender) ||
          (Boolean(activeChatWidget) && side === activeView) ||
          (side === "back" &&
            !frontImageUrl &&
            panelOffersRender &&
            renderSide === "front")
        }
        onOpenFullscreen={onOpenFullscreen}
        onEditInChat={!showInlineDetails ? onEditInChat : undefined}
      />
    );
  };
  const showMobileImagePreview = Boolean(
    mobileVisible &&
    !showInlineDetails &&
    !showMobileDetails &&
    images[activeView].url
  );
  return (
    <section
      id="designer-design-panel"
      aria-label="Dress design workspace"
      data-slot="desktop-design-workspace"
      data-mobile-image-preview={showMobileImagePreview ? "true" : undefined}
      className={cn(
        "designer-well designer-canvas-well h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:flex",
        mobileVisible ? "flex" : "hidden",
        className
      )}
    >
      {mobileAttentionHost &&
      !showInlineDetails &&
      (mobileVisible || chatHasActionablePanel)
        ? createPortal(
            <MobileAttentionPanel model={mobileAttentionModel} />,
            mobileAttentionHost
          )
        : null}
      {showInlineDetails ? (
        <DesignPreviewHeader
          titleId={titleId}
          description={
            [
              currentDesign?.primaryColor,
              currentDesign?.silhouette,
              currentDesign?.sleeve,
            ]
              .filter(Boolean)
              .join(" · ") || "Your dress preview will appear here."
          }
          detailsOpen={detailsOpen}
          detailsPanelId={detailsPanelId}
          detailsTriggerRef={detailsTriggerRef}
          onDetailsOpenChange={onDetailsOpenChange}
          startOverDisabled={startOverDisabled || apply.busy}
          onStartOver={showStartOver ? onStartOver : undefined}
        >
          {secondaryActions}
        </DesignPreviewHeader>
      ) : null}
      <div
        data-slot="desktop-design-workspace-body"
        className="designer-preview-body flex min-h-0 min-w-0 flex-1 gap-5"
      >
        <div
          data-slot="desktop-design-canvas"
          className="relative flex min-h-0 min-w-0 flex-1 flex-col"
          aria-busy={imageActionPending}
        >
          {!showInlineDetails ? (
            <>
              {(["front", "back"] as const).map(side => (
                <div
                  key={side}
                  id={`designer-${side}-panel`}
                  role="tabpanel"
                  aria-labelledby={`designer-mobile-tab-${side}`}
                  tabIndex={0}
                  hidden={showMobileDetails || activeView !== side}
                  className={cn(
                    "min-h-0 flex-1 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                    showMobileDetails || activeView !== side ? "hidden" : "flex"
                  )}
                >
                  {renderPortrait(side)}
                </div>
              ))}
              <div
                id={detailsPanelId}
                role="tabpanel"
                aria-labelledby="designer-mobile-tab-details"
                hidden={!showMobileDetails}
                className={cn(
                  "h-full min-h-0 flex-1",
                  !showMobileDetails && "hidden"
                )}
              >
                {inspector}
              </div>
            </>
          ) : !detailsOpen ? (
            <div
              className="designer-stage flex min-h-0 flex-1"
              data-slot="desktop-design-preview-stage"
            >
              {renderPortrait("front", true)}
              {backImageUrl || step === "back"
                ? renderPortrait("back", true)
                : null}
            </div>
          ) : (
            <Tabs
              value={activeView}
              onValueChange={selectTab}
              className="designer-view-tabs flex min-h-0 flex-1 flex-col gap-3"
            >
              <div className="flex h-11 shrink-0 items-center justify-between gap-3">
                <TabsList
                  aria-label="Dress design view"
                  className="designer-preview-view-list h-11! w-[184px]"
                >
                  <TabsTrigger
                    value="front"
                    className="designer-tab"
                    disabled={detailsEditing}
                    title={
                      detailsEditing
                        ? "Save or cancel your edits first."
                        : undefined
                    }
                  >
                    Front
                  </TabsTrigger>
                  <TabsTrigger
                    value="back"
                    className="designer-tab"
                    disabled={detailsEditing}
                    title={
                      detailsEditing
                        ? "Save or cancel your edits first."
                        : undefined
                    }
                  >
                    Back
                  </TabsTrigger>
                </TabsList>
                {showInlineDetails ? (
                  <span className="text-xs text-muted-foreground">
                    {images[activeView].url
                      ? `Saved ${activeView}`
                      : "No preview yet"}
                  </span>
                ) : null}
              </div>
              <TabsContent value="front" className="relative min-h-0">
                {renderPortrait("front")}
              </TabsContent>
              <TabsContent value="back" className="relative min-h-0">
                {renderPortrait("back")}
              </TabsContent>
            </Tabs>
          )}
          {imageActionPending &&
          (showInlineDetails || (!showMobileDetails && activeView === step)) ? (
            <div
              className={cn(
                "pointer-events-none absolute inset-x-3 z-10 flex justify-center",
                !showInlineDetails && !images[activeView].url
                  ? "top-1/2 -translate-y-1/2"
                  : "bottom-3"
              )}
            >
              <ChatGenerationProgress placement="canvas" />
            </div>
          ) : null}
        </div>
        {showInlineDetails && detailsOpen ? (
          <div
            id={detailsPanelId}
            data-slot="desktop-design-details-column"
            className="w-[min(360px,46%)] min-w-[260px] shrink-0 border-l border-border pl-5"
            onKeyDown={event => {
              if (event.key === "Escape" && !event.defaultPrevented) {
                event.preventDefault();
                onDetailsOpenChange(false);
                detailsTriggerRef.current?.focus();
              }
            }}
          >
            {inspector}
          </div>
        ) : null}
      </div>
      {!showInlineDetails && !showMobileDetails && frontImageUrl ? (
        <div className="flex shrink-0 gap-2" aria-label="Preview actions">
          {secondaryActions}
        </div>
      ) : null}
      {frontImageUrl ? (
        <FindSimilarDresses
          key={`${projectEpoch}:${agentSessionId}:${frontImageUrl}`}
          designSpec={searchPreviewSpec ?? apply.draft}
          usesSavedPreview={Boolean(searchPreviewSpec)}
        />
      ) : null}
      {showInlineDetails &&
      (quoteSubmitting ||
        operationError ||
        (tryOnStatus && tryOnStatus.status !== "idle")) ? (
        <div
          data-slot="workspace-operation-status"
          className="flex shrink-0 items-center justify-between gap-3 rounded-xl bg-muted px-3 py-2 text-xs"
        >
          <span
            role={
              operationError || tryOnStatus?.status === "error"
                ? "alert"
                : "status"
            }
          >
            {operationError ||
              (quoteSubmitting
                ? "Preparing your quote…"
                : tryOnStatus?.status === "generating"
                  ? "Creating your try-on…"
                  : tryOnStatus?.status === "complete"
                    ? "Your try-on is ready"
                    : tryOnStatus?.error)}
          </span>
          {!operationError && !quoteSubmitting && onViewTryOn ? (
            <Button
              className="designer-control"
              variant="ghost"
              onClick={onViewTryOn}
            >
              View try-on
            </Button>
          ) : null}
        </div>
      ) : null}
      {showInlineDetails &&
      (renderControl.hasAction ||
        detailsEditing ||
        hasPendingChanges ||
        imageActionPending ||
        apply.failedSide) ? (
        <DesktopDesignActions
          focusFallbackRef={detailsTriggerRef}
          summaryLabel={summaryLabel}
          summaryValue={summaryValue}
          failed={Boolean(apply.failedSide)}
          hasDesign={hasDesign}
          canUndo={
            apply.canUndo && !apply.busy && !detailsEditing && !continuing
          }
          onUndo={apply.undo}
          canRender={canRender}
          onRender={handleRender}
          renderLabel={designerRenderFooterLabel(
            renderControl,
            imageActionPending
          )}
          helper={actionReason}
          helperId={helperId}
        />
      ) : null}
      <PendingDesignActionDialog
        action={confirmation?.action ?? "tryOn"}
        open={confirmationCurrent}
        onRestoreFocus={() => {
          if (!document.querySelector('[role="dialog"][data-state="open"]'))
            secondaryTrigger.current?.focus();
        }}
        onOpenChange={open => {
          if (!open) dismissConfirmation();
        }}
        onUseGenerated={() => void continueWithGenerated()}
        onApplyFirst={() => void applyFirst()}
        creditCost={creditCost}
        applyDisabled={!canRender || continuing}
        applyReason={actionReason}
        generatedUnavailable={
          confirmation?.action === "quote" && !generatedSpec
        }
      />
    </section>
  );
}
